import { apiClient } from "@base/api/client";
import { HTTP_STATUS } from "@base/lib/error";
import { isAxiosApiError } from "@base/lib/utils";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import type { UseQueryOptions, UseSuspenseQueryOptions } from "@tanstack/react-query";
import { dailyLogDetailResponseSchema } from "./schema";
import type { DailyLogDetailResponse } from "./schema";

async function fetchDailyLogDetail(projectId: string, logId: string): Promise<DailyLogDetailResponse> {
  const response = await apiClient.get(`/project-management/api/projects/${projectId}/daily-logs/${logId}`);
  return dailyLogDetailResponseSchema.parse(response.data);
}

export function dailyLogDetailQueryOptions(projectId: string, logId: string) {
  return {
    queryKey: ["daily-log-detail", projectId, logId] as const,
    queryFn: () => fetchDailyLogDetail(projectId, logId),
  };
}

export function useDailyLogDetailQuery(
  projectId: string,
  logId: string,
  options?: Omit<UseSuspenseQueryOptions<DailyLogDetailResponse>, "queryKey" | "queryFn">,
) {
  return useSuspenseQuery({ ...dailyLogDetailQueryOptions(projectId, logId), ...options });
}

async function fetchLatestDailyLog(projectId: number): Promise<DailyLogDetailResponse | null> {
  try {
    const response = await apiClient.get(`/project-management/api/projects/${projectId}/daily-logs/latest`);
    return dailyLogDetailResponseSchema.parse(response.data);
  } catch (error: unknown) {
    if (isAxiosApiError(error)) {
      const { status, code } = error.response.data;

      if (status === HTTP_STATUS.BAD_REQUEST && code === "NO_RECENT_DAILY_LOG") {
        return null;
      }
    }
    throw error;
  }
}

export function latestDailyLogQueryOptions(projectId: number) {
  return {
    queryKey: ["latest-daily-log", projectId] as const,
    queryFn: () => fetchLatestDailyLog(projectId),
  };
}

export function useLatestDailyLogQuery(
  projectId: number,
  options?: Omit<UseQueryOptions<DailyLogDetailResponse | null>, "queryKey" | "queryFn">,
) {
  return useQuery({ ...latestDailyLogQueryOptions(projectId), ...options });
}
